/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useState, useCallback, useEffect } from 'react';

// @mui material components
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Icon from '@mui/material/Icon';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiButton from 'components/VuiButton';

// Vision UI Dashboard PRO React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

// React icons
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';

// Embla Carousel
import useEmblaCarousel from 'embla-carousel-react';

// API Services
import { takedownsApi } from 'services/takedownsApi';

function ReviewContent() {
  // State management
  const [reviewQueue, setReviewQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentContent, setCurrentContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [fading, setFading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showExplicitContent, setShowExplicitContent] = useState(false);

  // Carousel setup
  const images = currentContent?.IMAGES?.image_urls 
    ? currentContent.IMAGES.image_urls.split(',').map(url => url.trim())
    : [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: images.length > 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Set up the onSelect callback
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  // Reset explicit content overlay when content changes
  useEffect(() => {
    setShowExplicitContent(false);
  }, [currentContent]);

  // Load review queue on mount
  useEffect(() => {
    loadReviewQueue();
  }, []);

  // Load the review queue (20 items at a time)
  const loadReviewQueue = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await takedownsApi.getContentToReview(20, 0);
      
      setReviewQueue(data.ids || []);
      setPagination(data.pagination);
      setCurrentIndex(0);
      
      // Load first item if available
      if (data.ids && data.ids.length > 0) {
        await loadContentDetails(data.ids[0]);
      } else {
        setCurrentContent(null);
      }
    } catch (err) {
      setError('Failed to load review queue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load details for a specific content item
  const loadContentDetails = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      
      // Fetch content details and URLs in parallel
      const [details, urls] = await Promise.all([
        takedownsApi.getContentDetails(id),
        takedownsApi.fetchTakedownURLs(id).catch(() => ({ post_url: null })) // Graceful fallback
      ]);
      
      
      // Merge the data
      const contentWithUrls = {
        ...details,
        post_url: urls.post_url,
        tos_url: urls.tos_url
      };
      
      setCurrentContent(contentWithUrls);
    } catch (err) {
      setError('Failed to load content details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Move to next item with fade animation
  const moveToNext = async () => {
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < reviewQueue.length) {
      setCurrentIndex(nextIndex);
      await loadContentDetails(reviewQueue[nextIndex]);
    } else {
      // Reload queue when exhausted
      await loadReviewQueue();
    }
  };

  // Handle accept action
  const handleAccept = async () => {
    if (!currentContent || actionLoading) return;
    
    try {
      setActionLoading(true);
      setFading(true);
      
      await takedownsApi.acceptContent(currentContent.id);
      
      // Wait for fade animation
      setTimeout(async () => {
        setFading(false);
        await moveToNext();
        setActionLoading(false);
      }, 500);
    } catch (err) {
      setError('Failed to accept content. Please try again.');
      setFading(false);
      setActionLoading(false);
    }
  };

  // Handle hold action
  const handleHold = async () => {
    if (!currentContent || actionLoading) return;
    
    try {
      setActionLoading(true);
      setFading(true);
      
      await takedownsApi.holdContent(currentContent.id);
      
      // Wait for fade animation
      setTimeout(async () => {
        setFading(false);
        await moveToNext();
        setActionLoading(false);
      }, 500);
    } catch (err) {
      setError('Failed to put content on hold. Please try again.');
      setFading(false);
      setActionLoading(false);
    }
  };

  // Handle reject action
  const handleReject = async () => {
    if (!currentContent || actionLoading) return;
    
    try {
      setActionLoading(true);
      setFading(true);
      
      await takedownsApi.rejectContent(currentContent.id);
      
      // Wait for fade animation
      setTimeout(async () => {
        setFading(false);
        await moveToNext();
        setActionLoading(false);
      }, 500);
    } catch (err) {
      setError('Failed to reject content. Please try again.');
      setFading(false);
      setActionLoading(false);
    }
  };

  const totalToReview = pagination?.total_items || 0;
  const currentReviewCount = currentIndex + 1;

  // Loading state
  if (loading && !currentContent) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={2} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress color="info" />
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // No content state
  if (!loading && !currentContent && !error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={2}>
          <Alert severity="info">
            No content available for review at this time.
          </Alert>
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={2} sx={{ overflow: "visible", lineHeight: "1.2"}}>
        {/* Error Alert */}
        {error && (
          <VuiBox mb={2}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </VuiBox>
        )}

        {/* Header with Breadcrumb */}
        <VuiBox mb={2}>
            <VuiBox display="flex" justifyContent="space-between" alignItems="center">
              <VuiTypography variant="h4" color="white" fontWeight="medium">
                Review Content
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="medium">
                Total to Review: {currentReviewCount} of {totalToReview}
              </VuiTypography>
            </VuiBox>
        </VuiBox>

        {/* Main Content Card with Fade */}
        <Fade in={!fading} timeout={500}>
          <Card sx={{ overflow: "visible", lineHeight: "1.2" }}>
            <VuiBox p={2} sx={{ lineHeight: "1.2"}}>
              <Grid container spacing={2}>
              {/* Left Column - Content Information */}
              <Grid item xs={12} lg={7} sx={{ lineHeight: "1.2" , maxWidth: "80%"}}>
                <VuiBox>
                  {/* Content ID */}
                  {/*}
                  <VuiBox mb={2}>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">
                      CLIENTID-00002
                    </VuiTypography>
                  </VuiBox>
                  */}
                  
                  {/* Detection Info */}
                  <VuiBox mb={3}>
                    <VuiTypography 
                      variant="button" 
                      color="white" 
                      fontWeight="bold" 
                      mb={2}
                      sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                    >
                      DETECTION INFO
                    </VuiTypography>
                    
                    <VuiBox display="flex" flexDirection="column" gap={1} sx={{ lineHeight: "1.2" }}>
                      <VuiBox>
                        <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                          STATUS
                        </VuiTypography>
                        <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                          {currentContent?.STATUS || 'Pending'}
                        </VuiTypography>
                      </VuiBox>
                      {/*}
                      <VuiBox>
                        <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                          PRIORITY LEVEL
                        </VuiTypography>
                        <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                          High
                        </VuiTypography>
                      </VuiBox>
                      
                      <VuiBox>
                        <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                          DETECTION REASON
                        </VuiTypography>
                        <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5, maxWidth: '80%' }}>
                          AI Image Model that has been trained on the client's image and likeness. Currently posted across 3 platforms and has been downloaded 12,230 times.
                        </VuiTypography>
                      </VuiBox>
                      */}
                    </VuiBox>
                  </VuiBox>

                  {/* Divider */}
                  <Divider light sx={{ mx: 2, mb: 2 }} />
                

                  {/* Content Info and Stats Row */}
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={6}>
                      <VuiBox sx={{ lineHeight: "1.2" }}>
                        <VuiTypography 
                          variant="button" 
                          color="white" 
                          fontWeight="bold" 
                          mb={2}
                          sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                        >
                          CONTENT INFO
                        </VuiTypography>
                        
                        <VuiBox display="flex" flexDirection="column" gap={1}>
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              INFRINGEMENT TYPE
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.INFRINGEMENT_TYPE || 'N/A'}
                            </VuiTypography>
                          </VuiBox>
                          
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              MEDIA TYPE
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.MEDIA_TYPE || 'N/A'}
                            </VuiTypography>
                          </VuiBox>
                          {/*
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              INFRINGEMENT SCORE
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              90
                            </VuiTypography>
                          </VuiBox>
                          */}
                        </VuiBox>
                      </VuiBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <VuiBox>
                        <VuiTypography 
                          variant="button" 
                          color="white" 
                          fontWeight="bold" 
                          mb={2}
                          sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                        >
                          CONTENT STATS
                        </VuiTypography>
                        
                        <VuiBox display="flex" flexDirection="column" gap={1}>
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              Views
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.VIEWS?.toLocaleString() || '0'}
                            </VuiTypography>
                          </VuiBox>
                          
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              Comments
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.COMMENTS?.toLocaleString() || '0'}
                            </VuiTypography>
                          </VuiBox>
                          
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              LIKES
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.LIKES?.toLocaleString() || '0'}
                            </VuiTypography>
                          </VuiBox>
                          
                        </VuiBox>
                      </VuiBox>
                    </Grid>
                  </Grid>

                  {/* Platform and Threat Actor Row */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <VuiBox>
                        <VuiTypography 
                          variant="button" 
                          color="white" 
                          fontWeight="bold" 
                          mb={2}
                          sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                        >
                          PLATFORM
                        </VuiTypography>
                        
                        <VuiBox display="flex" flexDirection="column" gap={1}>
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              PLATFORM COUNT
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.PLATFORM_COUNT || 0}
                            </VuiTypography>
                          </VuiBox>
                          
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              PLATFORM NAME
                            </VuiTypography>
                            <VuiBox display="flex" flexDirection="column" gap={1} mt={0.5}>
                              {currentContent?.PLATFORM_NAME && currentContent.PLATFORM_NAME.length > 0 ? (
                                currentContent.PLATFORM_NAME.map((platform, idx) => (
                                  <VuiTypography key={idx} variant="button" color="white" fontWeight="regular">
                                    {platform} {currentContent?.post_url && (
                                      <>
                                        ({' '}
                                        <a 
                                          href={currentContent.post_url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
                                        >
                                          view post
                                        </a>
                                        {' '})
                                      </>
                                    )}
                                  </VuiTypography>
                                ))
                              ) : (
                                <VuiTypography variant="button" color="white" fontWeight="regular">
                                  N/A
                                </VuiTypography>
                              )}
                            </VuiBox>
                          </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <VuiBox>
                        <VuiTypography 
                          variant="button" 
                          color="white" 
                          fontWeight="bold" 
                          mb={2}
                          sx={{ fontSize: '14px', textTransform: 'uppercase' }}
                        >
                          THREAT ACTOR
                        </VuiTypography>
                        
                        <VuiBox display="flex" flexDirection="column" gap={1}>
                          <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              PREVIOUSLY DETECTED
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                               {currentContent?.PREVIOUSLY_DETECTED || 'No'}
                            </VuiTypography>
                            </VuiBox>
                            <VuiBox>
                            <VuiTypography variant="caption" color="text" fontWeight="medium" sx={{ fontSize: '10px', textTransform: 'uppercase' }}>
                              PROFILE
                            </VuiTypography>
                            <VuiTypography variant="button" color="white" fontWeight="regular" sx={{ display: 'block', mt: 0.5 }}>
                              {currentContent?.PROFILE?.username ? (
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                  @{currentContent.PROFILE.username}
                                </span>
                              ) : (
                                'N/A'
                              )}
                            </VuiTypography>
                            </VuiBox>
                        </VuiBox>
                      </VuiBox>
                    </Grid>
                  </Grid>
                </VuiBox>
              </Grid>

              {/* Right Column - Image Carousel */}
              <Grid item xs={12} lg={5}>
                <VuiBox position="relative" width="100%" maxWidth="100%">
                  {/* Embla Carousel Container */}
                  <VuiBox 
                    ref={emblaRef}
                    sx={{
                      overflow: 'hidden',
                      borderRadius: '15px',
                      height: '400px',
                      position: 'relative'
                    }}
                  >
                    <VuiBox 
                      sx={{
                        display: 'flex',
                        height: '100%'
                      }}
                    >
                      {images.map((image, index) => (
                        <VuiBox
                          key={index}
                          sx={{
                            flex: '0 0 100%',
                            minWidth: 0,
                            height: '400px',
                            backgroundImage: `url(${image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}
                        />
                      ))}
                    </VuiBox>

                    {/* Explicit Content Overlay */}
                    {currentContent?.INFRINGEMENT_TYPE === 'Explicit' && !showExplicitContent && (
                      <VuiBox
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 3,
                          borderRadius: '15px',
                          cursor: 'pointer',
                        }}
                        onClick={() => setShowExplicitContent(true)}
                      >
                        <Icon
                          sx={{
                            fontSize: '60px',
                            color: '#e51313',
                            mb: 2,
                          }}
                        >
                          warning
                        </Icon>
                        <VuiTypography
                          variant="h4"
                          color="white"
                          fontWeight="bold"
                          mb={1}
                          textAlign="center"
                        >
                          Explicit Content Warning
                        </VuiTypography>
                        <VuiTypography
                          variant="body2"
                          color="text"
                          textAlign="center"
                          sx={{ maxWidth: '300px' }}
                        >
                          This content may contain explicit material. Click to view.
                        </VuiTypography>
                      </VuiBox>
                    )}

                    {/* Navigation Arrows */}
                    <VuiBox 
                      position="absolute" 
                      right="20px" 
                      top="50%" 
                      transform="translateY(-50%)"
                      zIndex={2}
                    >
                      <IconButton
                        onClick={scrollNext}
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          },
                        }}
                      >
                        <IoChevronDown size="20px" style={{ transform: 'rotate(270deg)' }} />
                      </IconButton>
                    </VuiBox>

                    <VuiBox 
                      position="absolute" 
                      left="20px" 
                      top="50%" 
                      transform="translateY(-50%)"
                      zIndex={2}
                    >
                      <IconButton
                        onClick={scrollPrev}
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          color: 'white',
                          width: '40px',
                          height: '40px',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          },
                        }}
                      >
                        <IoChevronUp size="20px" style={{ transform: 'rotate(270deg)' }} />
                      </IconButton>
                    </VuiBox>
                  </VuiBox>

                  {/* Image Counter */}
                  <VuiBox textAlign="center" mt={2}>
                    <VuiTypography variant="button" color="white" fontWeight="regular">
                      PREVIEW {selectedIndex + 1} OF {images.length}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <VuiBox display="flex" justifyContent="center" gap={3} mt={4}>
              <VuiButton
                variant="outlined"
                color="error"
                onClick={handleReject}
                disabled={actionLoading}
                sx={{
                  border: '2px solid #e51313',
                  color: '#e51313',
                  padding: '12px 24px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'rgba(229, 19, 19, 0.1)',
                    border: '2px solid #e51313',
                  },
                }}
              >
                {actionLoading ? 'PROCESSING...' : 'REJECT'}
              </VuiButton>
              
              <VuiButton
                variant="outlined"
                color="white"
                onClick={handleHold}
                disabled={actionLoading}
                sx={{
                  border: '2px solid white',
                  color: 'white',
                  padding: '12px 24px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '2px solid white',
                  },
                }}
              >
                {actionLoading ? 'PROCESSING...' : 'HOLD'}
              </VuiButton>
              
              <VuiButton
                variant="outlined"
                color="success"
                onClick={handleAccept}
                disabled={actionLoading}
                sx={{
                  border: '2px solid #01b574',
                  color: '#01b574',
                  padding: '12px 24px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'rgba(1, 181, 116, 0.1)',
                    border: '2px solid #01b574',
                  },
                }}
              >
                {actionLoading ? 'PROCESSING...' : 'ACCEPT'}
              </VuiButton>
            </VuiBox>
          </VuiBox>
        </Card>
        </Fade>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ReviewContent;
